import { isConnectionComingSoon } from "@/config/connectionAvailability";
import { useRef, useState } from "react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { ConnectionBrandMark } from "./ConnectionBrandMark";
import ConnectionManageSurface from "./ConnectionManageSurface";

interface Props {
  provider: string;
  title: string;
  description: string;
}

/** Informational catalogue entry: never mounts account actions while disabled. */
export default function PreparedConnectionCard({ provider, title, description }: Props) {
  const { t } = useLanguage();
  const statusKey = isConnectionComingSoon(provider) ? "common.comingSoon" : "connections.ux.activationPending";
  const [detailsOpen, setDetailsOpen] = useState(false);
  const detailsButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <article data-provider-card={provider} data-availability="pending"
        className="flex min-h-[156px] flex-col rounded-2xl border border-border/80 bg-card p-4 shadow-sm sm:p-5">
        <div className="flex min-w-0 items-start gap-3">
          <ConnectionBrandMark provider={provider} />
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-foreground">{title}</h2>
            <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-muted-foreground">{description}</p>
          </div>
        </div>
        <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-4">
          <span className="text-xs font-medium text-muted-foreground">{t(statusKey)}</span>
          <button ref={detailsButtonRef} type="button" onClick={() => setDetailsOpen(true)}
            aria-label={t("connections.ux.viewDetails") + " — " + title}
            className="inline-flex min-h-11 items-center justify-center rounded-xl border border-border px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
            {t("connections.ux.viewDetails")}
          </button>
        </div>
      </article>
      <ConnectionManageSurface open={detailsOpen} onOpenChange={setDetailsOpen}
        title={title} description={description} closeLabel={t("common.close")} returnFocusRef={detailsButtonRef}
        brand={<ConnectionBrandMark provider={provider} size="lg" />}>
        <div className="space-y-3 pt-1">
          <p className="text-sm font-semibold text-foreground">{t(statusKey)}</p>
          <p className="text-sm leading-relaxed text-muted-foreground">{t("connections.ux.activationPendingDescription")}</p>
        </div>
      </ConnectionManageSurface>
    </>
  );
}
